<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

export interface AiComposerDropdownOption {
  id: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    options: AiComposerDropdownOption[];
    disabled?: boolean;
    title?: string;
  }>(),
  {
    disabled: false,
    title: '',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const open = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

const currentLabel = computed(() => {
  const hit = props.options.find((o) => o.id === props.modelValue);
  return hit?.label ?? props.modelValue;
});

function updateMenuPosition() {
  const el = triggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  menuStyle.value = {
    position: 'fixed',
    left: `${Math.max(8, rect.left)}px`,
    bottom: `${window.innerHeight - rect.top + 4}px`,
    minWidth: `${Math.max(rect.width, 88)}px`,
    maxWidth: `${Math.min(220, window.innerWidth - 16)}px`,
    zIndex: '10050',
  };
}

async function toggle() {
  if (props.disabled) return;
  if (!open.value) {
    await nextTick();
    updateMenuPosition();
  }
  open.value = !open.value;
}

function select(id: string) {
  if (id === props.modelValue) {
    open.value = false;
    return;
  }
  emit('update:modelValue', id);
  open.value = false;
}

function onDocPointerDown(e: PointerEvent) {
  if (!open.value) return;
  const target = e.target as Node | null;
  if (rootRef.value?.contains(target)) return;
  if (menuRef.value?.contains(target)) return;
  open.value = false;
}

function onViewportChange() {
  if (open.value) updateMenuPosition();
}

watch(
  () => props.options.map((o) => o.id).join('\0'),
  () => {
    if (!props.options.some((o) => o.id === props.modelValue) && props.options.length) {
      emit('update:modelValue', props.options[0].id);
    }
  },
);

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown, true);
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true);
  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('scroll', onViewportChange, true);
});
</script>

<template>
  <div
    ref="rootRef"
    class="ai-composer-dropdown"
    :class="{ 'is-open': open, 'is-disabled': disabled }"
  >
    <button
      ref="triggerRef"
      type="button"
      class="ai-composer-dropdown-trigger"
      :title="title"
      :disabled="disabled"
      :aria-expanded="open"
      @click.stop="toggle"
    >
      <span class="ai-composer-dropdown-label">{{ currentLabel }}</span>
      <span class="ai-composer-dropdown-caret" aria-hidden="true">▾</span>
    </button>
    <Teleport to="body">
      <div
        v-if="open && !disabled"
        ref="menuRef"
        class="ai-composer-dropdown-menu"
        :style="menuStyle"
        role="listbox"
        @click.stop
        @pointerdown.stop
      >
        <button
          v-for="opt in options"
          :key="opt.id"
          type="button"
          class="ai-composer-dropdown-item"
          :class="{ 'is-active': opt.id === modelValue }"
          role="option"
          :aria-selected="opt.id === modelValue"
          @pointerdown.stop.prevent="select(opt.id)"
        >
          {{ opt.label }}
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.ai-composer-dropdown {
  position: relative;
  flex-shrink: 0;
}

.ai-composer-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 24px;
  max-width: 108px;
  padding: 0 6px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
  color: #4e5969;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  box-sizing: border-box;
}

.ai-composer-dropdown-trigger:hover:not(:disabled) {
  border-color: #c9cdd4;
  background: #fff;
}

.ai-composer-dropdown-trigger:focus-visible {
  outline: none;
  border-color: #0052d9;
}

.ai-composer-dropdown.is-open .ai-composer-dropdown-trigger {
  border-color: #0052d9;
}

.ai-composer-dropdown.is-disabled .ai-composer-dropdown-trigger {
  cursor: not-allowed;
  opacity: 0.55;
  background: #f2f3f5;
}

.ai-composer-dropdown-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 84px;
}

.ai-composer-dropdown-caret {
  flex-shrink: 0;
  font-size: 10px;
  color: #86909c;
  transform: rotate(180deg);
}

.ai-composer-dropdown-menu {
  padding: 4px;
  border-radius: 6px;
  border: 1px solid #e5e6eb;
  background: #fff;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.ai-composer-dropdown-item {
  display: block;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #1f2329;
  font-size: 11px;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
}

.ai-composer-dropdown-item:hover {
  background: #f2f3f5;
}

.ai-composer-dropdown-item.is-active {
  color: #0052d9;
  background: #e8f0fe;
  font-weight: 500;
}
</style>
